/**
 * The realtime client for `/ws` — one JWT-authenticated WebSocket per session
 * carrying server→client pushes the edge fans out over Dragonfly pub/sub
 * (`apps/api` `routes/ws.ts` + `apps/backend` `ws-fanout.ts`): a proactive
 * message ("she texts you first"), a new turn synced from another device, a
 * conversation title change, a credit-balance update. The surface shell wires
 * each to a `Toast` / a thread refresh / the switcher.
 *
 * Split for testability (the frontend has no DOM in `bun test`): the message
 * schema + {@link parseWsMessage} + {@link dispatchWsMessage} are pure, and the
 * connection manager takes an injected socket factory + scheduler (DIP) so its
 * reconnect/backoff logic is exercised with a fake socket and no real network.
 *
 * The client RE-VALIDATES every payload against the Zod union at the boundary
 * (the edge is the authority, but a malformed frame is dropped, never dispatched)
 * — the same discipline as `api/types.ts` for REST bodies.
 */
import { MediaJobEvent } from '@4f/shared';
import { z } from 'zod';

/**
 * A proactive turn pushed to every connected client — the exact payload the
 * backend push service serializes (`serializePushPayload`). Carries the ids so a
 * live tab can surface the new message and refresh her thread; `title`/`body`
 * are the notification copy (also read by the service worker for a device push).
 */
export const WsProactiveMessage = z.object({
  type: z.literal('proactive_message'),
  title: z.string().optional(),
  body: z.string().optional(),
  girlfriendId: z.string().min(1),
  conversationId: z.string().min(1),
  messageId: z.string().min(1),
});
export type WsProactiveMessage = z.infer<typeof WsProactiveMessage>;

/** A turn persisted on another device — cross-device sync of an open thread. */
export const WsMessageCreated = z.object({
  type: z.literal('message'),
  girlfriendId: z.string().min(1).optional(),
  conversationId: z.string().min(1),
  messageId: z.string().min(1),
});
export type WsMessageCreated = z.infer<typeof WsMessageCreated>;

/** A conversation's title was (re)generated — update it live in the header/switcher. */
export const WsTitleUpdated = z.object({
  type: z.literal('title'),
  conversationId: z.string().min(1),
  title: z.string(),
});
export type WsTitleUpdated = z.infer<typeof WsTitleUpdated>;

/** The account's credit balance changed (spend/grant) — surface it as a `Toast`. */
export const WsCreditUpdated = z.object({
  type: z.literal('credit'),
  balanceMicros: z.number(),
});
export type WsCreditUpdated = z.infer<typeof WsCreditUpdated>;

/**
 * A generation job's status push — the WS-relay completion swap (PR8 F6): the
 * client matches `jobId` against its live inline placeholder (`MediaRef.jobId`)
 * and, on `done`, re-fetches the companion's gallery to resolve `assetId` to a
 * fetchable URL (`resolveMediaAssets` in `stream-model.ts`).
 *
 * This is `@4f/shared`'s {@link MediaJobEvent} verbatim — the SAME schema the
 * `apps/jobs` media publisher serializes (`media/progress.ts`), not a hand-copied
 * mirror of it. A locally-retyped frame is how a producer and a consumer drift
 * until the event is silently dropped; here the two cannot disagree.
 */
export const WsMediaJobUpdate = MediaJobEvent;
export type WsMediaJobUpdate = MediaJobEvent;

/**
 * The account realtime channel's payload set (`ws-fanout.ts` documents exactly
 * these events). Discriminated on `type`; a new server event = a new member
 * + a new handler, with no change to existing dispatch (OCP). `proactive_message`
 * (the push relay) and `media_job` (the media worker's progress publisher) are
 * published today; the rest land with their backend publishers and are already
 * parsed + dispatched here.
 */
export const WsMessage = z.discriminatedUnion('type', [
  WsProactiveMessage,
  WsMessageCreated,
  WsTitleUpdated,
  WsCreditUpdated,
  WsMediaJobUpdate,
]);
export type WsMessage = z.infer<typeof WsMessage>;

/** Parse + validate one raw frame; a non-JSON or off-contract frame is dropped (`null`). */
export function parseWsMessage(raw: string): WsMessage | null {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  const parsed = WsMessage.safeParse(json);
  return parsed.success ? parsed.data : null;
}

/** The per-event callbacks the surface shell wires (all optional). */
export interface RealtimeHandlers {
  /** A proactive turn arrived — toast + refresh her thread. */
  onProactiveMessage?(message: WsProactiveMessage): void;
  /** A turn was persisted elsewhere — sync the open thread. */
  onMessageCreated?(message: WsMessageCreated): void;
  /** A conversation title changed. */
  onTitleUpdated?(message: WsTitleUpdated): void;
  /** The credit balance changed. */
  onCreditUpdated?(message: WsCreditUpdated): void;
  /** A generation job's status changed — swap the placeholder on `done`. */
  onMediaJobUpdate?(message: WsMediaJobUpdate): void;
}

/** Route a parsed message to its handler; the union is switched exhaustively. */
export function dispatchWsMessage(message: WsMessage, handlers: RealtimeHandlers): void {
  switch (message.type) {
    case 'proactive_message':
      handlers.onProactiveMessage?.(message);
      return;
    case 'message':
      handlers.onMessageCreated?.(message);
      return;
    case 'title':
      handlers.onTitleUpdated?.(message);
      return;
    case 'credit':
      handlers.onCreditUpdated?.(message);
      return;
    case 'media_job':
      handlers.onMediaJobUpdate?.(message);
      return;
  }
}

/**
 * Whether a `media_job` frame means NEW MEDIA is now fetchable — the only reason
 * to re-read (and re-presign) the companion's gallery.
 *
 * The worker beats `running` once and `segment_done` once PER SEGMENT (up to 12
 * for a 60s clip), all of which project to `status: 'running'`. Treating every
 * frame as "new media landed" costs ~14 full gallery reads per video in every
 * open tab and learns nothing: `media_assets` is written only in the same step
 * that flips the job `done` (media-pipeline.md § "No orphan assets").
 */
export function mediaJobLanded(update: WsMediaJobUpdate): boolean {
  return update.status === 'done';
}

/**
 * The minimal socket surface the manager drives — the browser `WebSocket`
 * satisfies it.
 *
 * There is no `send`, on purpose: `/ws` is PUSH-ONLY and the edge hangs up with
 * `1003 Unsupported Data` on the first inbound frame (`apps/api` `routes/ws.ts`).
 * Leaving the method off the port is what makes that a compile error here instead
 * of a silently dropped socket in production.
 */
export interface WebSocketLike {
  close(code?: number, reason?: string): void;
  addEventListener(type: string, listener: (event: unknown) => void): void;
  removeEventListener(type: string, listener: (event: unknown) => void): void;
}

/**
 * Opens a socket for a URL + subprotocol list (DIP) — defaults to the browser
 * `WebSocket`. The protocols are how the bearer reaches the edge; see
 * {@link bearerSubprotocol}.
 */
export type SocketFactory = (url: string, protocols: readonly string[]) => WebSocketLike;

/** Schedule `fn` after `ms`; returns a canceller (injectable for tests). */
export type Scheduler = (fn: () => void, ms: number) => () => void;

export interface RealtimeOptions {
  /** The `/ws` endpoint (same-origin `'/ws'` behind the dev front door, or absolute). */
  readonly url: string;
  /** Bearer supplier — a WS can't set a header, so it rides the `bearer` subprotocol. */
  readonly token: () => string | null;
  readonly handlers: RealtimeHandlers;
  /** Socket opener; defaults to `new WebSocket(url)`. */
  readonly socketFactory?: SocketFactory;
  /** Timer source; defaults to `setTimeout`/`clearTimeout`. */
  readonly schedule?: Scheduler;
  /** Reconnect backoff ladder (ms); the last value repeats. Default 1s→2s→5s→10s→30s→60s. */
  readonly backoff?: readonly number[];
  /**
   * Renew the session before re-dialling. Resolves `true` when a usable token is
   * now in place. Injected (DIP) — the composition root points it at the api
   * client's single-flight `renewSession()`, so a socket renewal and an HTTP 401
   * recovery can never fire two concurrent refreshes (the edge treats a superseded
   * refresh token as REUSE and revokes the whole family).
   *
   * It MUST settle: the ladder waits on it before the next dial, so one that
   * hangs holds realtime down with it. The client's is deadlined for exactly that.
   */
  readonly renew?: () => Promise<boolean>;
}

/** The realtime connection handle the surface owns. */
export interface Realtime {
  /** Open the socket (no-op while already open, or when no token is available yet). */
  connect(): void;
  /** Close the socket and stop reconnecting (idempotent). */
  close(): void;
  /** Whether a socket is currently open. */
  readonly connected: () => boolean;
}

/**
 * Reconnect ladder (ms); the LAST value repeats forever. `attempt` resets on a
 * socket that actually opened and on an explicit {@link Realtime.connect}, so a
 * transient drop replays from 1s while a handshake the server keeps REFUSING
 * walks to the tail and stays there.
 *
 * The tail is long on purpose. A browser cannot read the status of a failed WS
 * upgrade — the `/ws` gate's `403` surfaces as `error` + close `1006`,
 * indistinguishable from a dropped network — so a client the gate rejects can
 * only keep retrying. The reachable case is a BANNED account
 * (`assertAccountEnabled` → `account_disabled`): `AuthGate` never mounts the
 * surface for an un-verified session, so the age `403` is defense-in-depth for a
 * non-SPA client, not a storm this app can produce. Saturating at 10s made a
 * banned tab a permanent 6-requests-per-minute storm; 60s bounds it without
 * slowing genuine recovery, which `connect()` on `online` short-circuits anyway.
 */
const DEFAULT_BACKOFF = [1_000, 2_000, 5_000, 10_000, 30_000, 60_000] as const;

/**
 * RFC 6455 `1013 Try Again Later` — the edge's "not now, and not soon". It has
 * exactly two senders (`apps/api` `routes/ws.ts`), and both want the same answer:
 *
 *   - this account is already at its per-pod socket ceiling
 *     (`apps/api` `http/socket-limit.ts`) — the slot frees when another tab closes;
 *   - the edge's `SUBSCRIBE` never acked, i.e. Dragonfly is unreachable POD-WIDE,
 *     which every pod behind the load balancer shares. Re-dialling fast there does
 *     not find a healthy pod, it just makes every connected client re-run the `/ws`
 *     JWT gate (and its `accounts` read) every few seconds for the whole outage.
 *
 * It is the one refusal a browser CAN read: the handshake succeeded, so `open`
 * fired and reset `attempt`, and only then did the server close. Retried naively
 * that is a 1-request-per-second loop — a client-side storm created by the very
 * mechanisms meant to bound load. A refusal that says "later" is honoured by
 * jumping straight to the ladder's tail; a minute is a fine time to find out.
 * (`1011`, by contrast, means THIS pod is draining — that one keeps the fast
 * ladder, because a prompt re-dial genuinely does land on a healthy sibling.)
 */
const WS_TRY_AGAIN_LATER = 1013;

const defaultScheduler: Scheduler = (fn, ms) => {
  const handle = setTimeout(fn, ms);
  return () => clearTimeout(handle);
};

const defaultFactory: SocketFactory = (url, protocols) =>
  new WebSocket(url, [...protocols]) as unknown as WebSocketLike;

/**
 * The subprotocol list that carries the bearer to `/ws`:
 * `Sec-WebSocket-Protocol: bearer, <token>`.
 *
 * The `WebSocket` constructor's second argument is the ONLY request header a
 * browser lets a page set on an upgrade, which makes it the only way to send a
 * credential that is not the URL — and a URL is copied verbatim into the ingress
 * access log, the browser's own history, a `Referer` and every proxy in between,
 * none of which redact it and all of which outlive the token. The edge reads the
 * second entry (`apps/api` `routes/ws.ts` `extractWsToken`); the server answers
 * the handshake by selecting the first, so `bearer` — never the token — is what
 * comes back in the response.
 *
 * No encoding: every character a JWT can contain (base64url plus `.`) is already
 * a legal subprotocol token.
 */
export function bearerSubprotocol(token: string): readonly string[] {
  return ['bearer', token];
}

/** Read the string frame off a `message` event (browser `MessageEvent.data`). */
function frameData(event: unknown): string | null {
  const data = (event as { data?: unknown }).data;
  return typeof data === 'string' ? data : null;
}

/** Read the close code off a `close` event (browser `CloseEvent.code`); `error` has none. */
function closeCode(event: unknown): number | null {
  const code = (event as { code?: unknown }).code;
  return typeof code === 'number' ? code : null;
}

/**
 * Build the reconnecting realtime client. Lifecycle: `connect` opens a socket
 * (only when a token exists — pre-auth it is a no-op, so dev never storms an
 * unauthenticated upgrade); a drop schedules a reconnect on the backoff ladder;
 * `close` stops reconnecting. Every open re-reads the token so a refreshed
 * session reconnects with the current one. When `options.renew` is wired, see
 * `open` below for how a refused handshake renews before the next dial.
 */
export function createRealtime(options: RealtimeOptions): Realtime {
  const factory = options.socketFactory ?? defaultFactory;
  const schedule = options.schedule ?? defaultScheduler;
  const backoff = options.backoff ?? DEFAULT_BACKOFF;

  let socket: WebSocketLike | null = null;
  let cancelReconnect: (() => void) | null = null;
  let attempt = 0;
  let stopped = false;
  let isConnected = false;
  // Set when a dialled socket goes down WITHOUT ever firing `open` — the shape
  // a pre-upgrade rejection takes (it surfaces as `error` + close `1006`,
  // indistinguishable from a network drop). Cleared the instant a socket DOES
  // open. Only consulted when `options.renew` is wired — see `open`.
  let handshakeRefused = false;
  // Re-entrancy guard: a concurrent connect()/reconnect must not start a SECOND
  // renew() — the edge treats a superseded refresh token as reuse and revokes the
  // whole family, so two in-flight renewals is a self-inflicted logout.
  let renewing = false;

  const clearReconnect = (): void => {
    cancelReconnect?.();
    cancelReconnect = null;
  };

  const scheduleReconnect = (): void => {
    if (stopped || cancelReconnect) {
      return;
    }
    const delay = backoff[Math.min(attempt, backoff.length - 1)] ?? DEFAULT_BACKOFF[0];
    attempt += 1;
    cancelReconnect = schedule(() => {
      cancelReconnect = null;
      open();
    }, delay);
  };

  /**
   * Dial a socket for a KNOWN-present token — shared by `open`'s direct path
   * and its post-renewal continuation.
   */
  const dial = (token: string): void => {
    let ws: WebSocketLike;
    try {
      ws = factory(options.url, bearerSubprotocol(token));
    } catch {
      // `new WebSocket(url, protocols)` THROWS rather than firing `error` for a
      // handshake the browser refuses outright — a mixed-content `ws://` from an
      // https page, a CSP `connect-src` refusal, a malformed origin, or a token
      // that is not a legal subprotocol token. This call also runs from
      // inside the reconnect timer, where `cancelReconnect` has already been
      // cleared and `socket` is still null: an escaping throw leaves NO timer, NO
      // socket, and realtime is silently dead for the rest of the session — no
      // proactive messages, no cross-device sync, no media-completion beat, with
      // the UI still showing a healthy thread. Treat it as a failed attempt and
      // take the next rung, exactly like a refused handshake.
      scheduleReconnect();
      return;
    }
    socket = ws;
    // Scoped to THIS dial; only this closure's `onDown` below reads it.
    let everOpened = false;

    // Every listener below is guarded on `socket === ws`, not just `onDown`. The
    // browser keeps delivering events to a handle we have already abandoned (its
    // Close frame is still in flight, or `close()` was called on a half-open
    // socket), and `CompanionSurface` disposes the chat store BEFORE
    // `realtime.close()` — so an unguarded `message` dispatches into a disposed
    // store, and an unguarded `open` resets `attempt` to 0, silently undoing the
    // backoff the LIVE ladder is walking.
    ws.addEventListener('open', () => {
      if (socket !== ws) {
        return;
      }
      attempt = 0;
      isConnected = true;
      everOpened = true;
      handshakeRefused = false;
    });
    ws.addEventListener('message', (event) => {
      if (socket !== ws) {
        return;
      }
      const raw = frameData(event);
      if (raw === null) {
        return;
      }
      const message = parseWsMessage(raw);
      if (message) {
        dispatchWsMessage(message, options.handlers);
      }
    });
    const onDown = (event: unknown): void => {
      if (socket === ws) {
        socket = null;
        isConnected = false;
        if (!everOpened) {
          // Refused before it ever opened — the next rung renews. See `open`.
          handshakeRefused = true;
        }
        // Drop the dead handle explicitly. On a `close` event this is already a
        // no-op; on an `error` it stops the browser holding a socket and its
        // buffers for a handle nothing reads any more, and where the transport is
        // still alive the Close frame reaches the edge, which frees the account's
        // slot at once instead of on the server's idle timeout.
        //
        // It does NOT rescue the case that motivates it: a socket left half-open
        // by a network transition has no path to the server, so that peer holds
        // its slot until Bun's WS `idleTimeout` reaps it whatever this client
        // does. That residue is precisely why the edge sizes the per-account
        // ceiling for the reconnect OVERLAP (`apps/api` `http/socket-limit.ts`)
        // rather than for the steady state.
        ws.close();
        if (closeCode(event) === WS_TRY_AGAIN_LATER) {
          // The edge refused us on purpose — we are at the account's socket
          // ceiling, or its pub/sub backend is down for every pod alike. Neither
          // is fixed by re-dialling in a second, and both are made worse by it, so
          // skip to the ladder's tail and poll once a minute instead.
          attempt = Math.max(0, backoff.length - 1);
        }
        scheduleReconnect();
      }
    };
    ws.addEventListener('close', onDown);
    ws.addEventListener('error', onDown);
  };

  /**
   * Dial straight away, or renew first, via `dial`'s post-renewal
   * continuation below. Gated on a REFUSED handshake, not fired every rung: a
   * healthy reconnect must not pay a `renew()` round-trip before trying the
   * token it already holds. Only "no token" or "last attempt never opened"
   * make the current token untrustworthy enough to wait for.
   */
  const open = (): void => {
    if (stopped || socket || renewing) {
      return;
    }
    const token = options.token();
    const renew = options.renew;
    if (renew && (!token || handshakeRefused)) {
      renewing = true;
      const settle = (): void => {
        renewing = false;
        // Tested either way now — don't re-renew next rung for this SAME refusal.
        handshakeRefused = false;
        if (stopped || socket) {
          // close() ran mid-renewal, or another caller already dialled.
          return;
        }
        const refreshed = options.token();
        if (!refreshed) {
          // Session is gone; connect() restarts the ladder once one exists —
          // the same "pre-auth is a no-op" semantics as the branch below.
          return;
        }
        // Dial anyway even on a `false` resolve: the refusal may not have been
        // the token's fault (the edge was briefly unreachable), and trying the
        // token we have is exactly what today's code does.
        dial(refreshed);
      };
      // A rejection is treated like a `false` resolve — re-read + dial either way
      // — so an escaping one would kill realtime silently, the same class of
      // defect the socket-factory `try/catch` above guards.
      renew().then(settle, settle);
      return;
    }
    if (!token) {
      // Pre-auth: nothing to authenticate the upgrade with — wait for connect() to
      // be called again once a session token exists rather than loop on 401s.
      return;
    }
    dial(token);
  };

  return {
    connect(): void {
      stopped = false;
      // An explicit `connect()` is a fresh intent — the surface mounted, or the
      // browser just fired `online` — so it REPLAYS the ladder from the first
      // rung and drops any pending timer. Without this reset, a call made while
      // the ladder had walked to its tail would still wait a full tail-interval
      // on the next failure, which is exactly the case the caller is trying to
      // shorten. `open()` is a no-op while a socket is live.
      clearReconnect();
      attempt = 0;
      open();
    },
    close(): void {
      stopped = true;
      clearReconnect();
      attempt = 0;
      isConnected = false;
      const current = socket;
      socket = null;
      current?.close();
    },
    connected: () => isConnected,
  };
}
